import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {ErrorsWrapper, Error} from 'components/pages/DocumentsDetails/styled';
import {Link} from 'components/ui-kit/Link';
import {ContentColumns} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {ROUTE} from 'core/routes';
import {storages$} from 'models/documents/returns';

import {ReturnGoodsReport} from 'types';
import {toHex} from 'utils/string';

import {
    MustFillSection,
    ProductCard,
    ProductCode,
    ProductIconWrapper,
    ProductInfo,
    ProductMeta,
    Section,
    SectionTitle,
} from '../styled';
import {formatReturnGoodsDate} from '../utils';

type Props = {
    data: ReturnGoodsReport;
    showStorageLocation: boolean;
    storageLocation: number | null;
    onStorageLocationChange: (value: number | null) => void;
};

export const GeneralInfo = ({
    data,
    showStorageLocation,
    storageLocation,
    onStorageLocationChange,
}: Props) => {
    // const commonDataState = useStore(commonDataState$);
    // const {data} = commonDataState;
    const returnInfo = (data?.reportInfo as unknown) as ReturnGoodsReport['reportInfo'];

    const {t} = useTranslation();
    const translate = (prefix: 'page' | 'dictionary', key?: string | null) => {
        if (!key) return '-';

        if (prefix === 'page') {
            return t(`pages.documents.returnGoods.${key}`);
        }

        return t(`dictionaries.documents.returnGoods.${key}`);
    };
    const storages = useStore(storages$);

    const storageOptions = useMemo<SelectOption<number>[]>(
        () =>
            storages.map((storage) => ({
                value: storage.id,
                label: storage.storageName ?? '',
            })),
        [storages],
    );

    const selectedStorage =
        storageOptions.find((option) => option.value === storageLocation) ?? null;
    const productLink = returnInfo?.markingCode
        ? generatePath(ROUTE.PRODUCTS_DETAIL, {
              id: toHex(`${returnInfo.productCode ?? null}§${returnInfo.markingCode}`),
          })
        : null;
    const generalInfo = [
        {
            returnDate: formatReturnGoodsDate(returnInfo?.returnDate, 'dd.MM.yyyy'),
        },
        {
            productCondition: translate(
                'dictionary',
                returnInfo?.productCondition && `productCondition.${returnInfo.productCondition}`,
            ),
        },
        {
            referenceDocNumber: returnInfo?.referenceDocumentNumber ?? '-',
        },
        ...(returnInfo?.disposalReason
            ? [
                  {
                      disposalReason: translate(
                          'dictionary',
                          `reasons.${returnInfo.disposalReason}`,
                      ),
                  },
              ]
            : []),
        ...(returnInfo?.comment ? [{comment: returnInfo.comment}] : []),
    ];

    return (
        <>
            <ErrorsWrapper>
                {data.errors?.map((err, index) => (
                    <Error key={index}>{err.message ?? ''}</Error>
                ))}
            </ErrorsWrapper>
            {showStorageLocation && (
                <MustFillSection>
                    <SectionTitle>{translate('page', 'mustFillOut')}</SectionTitle>
                    <Select
                        isRequired
                        placeholder={translate('page', 'form.storageLocationPlaceholder')}
                        label={translate('page', 'form.storageLocationLabel')}
                        options={storageOptions}
                        value={selectedStorage}
                        onChange={(val: SelectOption<number>) => {
                            onStorageLocationChange(val?.value ?? null);
                        }}
                    />
                </MustFillSection>
            )}

            <Section>
                <SectionTitle>{translate('page', 'generalInfo')}</SectionTitle>
                <ContentColumns
                    md={3}
                    data={generalInfo}
                    tNameSpace="pages.documents.returnGoods.details"
                />
            </Section>

            <Section>
                <SectionTitle>{translate('page', 'product')}</SectionTitle>
                <ProductCard>
                    <ProductIconWrapper>
                        <ProductGroupIcon
                            style={{margin: 0, padding: 0}}
                            value={returnInfo?.productGroupCode}
                            width={16}
                        />
                    </ProductIconWrapper>
                    <ProductInfo>
                        {productLink ? (
                            <Link
                                to={productLink}
                                style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    lineHeight: '21px',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {returnInfo.markingCode}
                            </Link>
                        ) : (
                            <ProductCode>-</ProductCode>
                        )}
                        <ProductMeta>
                            <span>
                                {translate('page', 'productName')}: {returnInfo?.productName ?? '-'}
                            </span>
                            <span>
                                {translate('page', 'productCode')}: {returnInfo?.productCode ?? '-'}
                            </span>
                        </ProductMeta>
                    </ProductInfo>
                </ProductCard>
            </Section>
        </>
    );
};
